import type { InterventionStatus } from '@arhs-fleetback/types/entities/interventions';

import { DEFAULT_STATUS, INTERVENTIONS_API_URL } from 'constants/InterventionConstants';

import type { DetailedChecklist } from 'modules/checklists/types/projections/detailed-checklist';
import type { ErrorAction, PageContent } from 'modules/global/generic.types';
import type { ModeEnum } from 'modules/global/typing/enums/modes.enum';
import * as actionTypes from 'modules/interventions/actionTypes';

import type * as types from '../types/actions';
import type { DetailedIntervention } from '../types/projections/detailed-intervention';
import type { PaginatedIntervention } from '../types/projections/paginated-intervention';
import type { InterventionCloneOptions } from '../types/requests/intervention-clone-options';
import type { InterventionsCreateRequest } from '../types/requests/interventions-create-request';
import type { InterventionsFilterRequest } from '../types/requests/interventions-filter-request';

/** Modes that map to an interventions API base URL (excludes AUDIT/REMINDERS). */
type InterventionApiMode = keyof typeof INTERVENTIONS_API_URL;

// FETCH OPERATIONS
export const fetchInterventions = (
  params: InterventionsFilterRequest & { mode: InterventionApiMode },
  meta: Record<string, unknown> = {},
): types.FetchInterventionsRequestAction => ({
  type: actionTypes.FETCH_INTERVENTIONS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[params.mode]}/filter`,
      params: {
        status: DEFAULT_STATUS,
        ...params,
      },
    },
  },
  meta,
});

export const fetchInterventionsFailure = (error: string, meta: Record<string, unknown> = {}): types.FetchInterventionsFailureAction => ({
  type: actionTypes.FETCH_INTERVENTIONS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchInterventionsSuccess = (
  interventions: PageContent<PaginatedIntervention>,
  meta: Record<string, unknown> = {},
): types.FetchInterventionsSuccessAction => ({
  type: actionTypes.FETCH_INTERVENTIONS_SUCCESS,
  payload: interventions,
  meta,
});

export const fetchIntervention = (
  interventionId: string,
  mode: InterventionApiMode,
  meta: Record<string, unknown> = {},
): types.FetchInterventionRequestAction => ({
  type: actionTypes.FETCH_INTERVENTION_REQUEST,
  payload: {
    id: interventionId,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}`,
      params: {
        projection: 'details',
      },
    },
  },
  meta,
});

export const fetchInterventionSuccess = (
  intervention: DetailedIntervention,
  meta?: Record<string, unknown>,
): types.FetchInterventionSuccessAction => ({
  type: actionTypes.FETCH_INTERVENTION_SUCCESS,
  payload: intervention,
  meta,
});

export const fetchInterventionFailure = (
  message: string,
  meta?: Record<string, unknown>,
): types.FetchInterventionFailureAction => ({
  type: actionTypes.FETCH_INTERVENTION_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const genericFetchInterventions = (
  { mode, ...params }: InterventionsFilterRequest & { mode: InterventionApiMode },
  meta: Record<string, unknown> = {},
): types.GenericFetchInterventionsAction => ({
  type: actionTypes.INTERVENTIONS_FETCH,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode]}/filter`,
      params: {
        projection: 'page',
        sort: 'startDate,desc',
        status: ['ANSWERED', 'OPEN', 'CLOSED', 'READ', 'SENT', 'TO_BE_SENT'],
        mode,
        ...params,
      },
    },
  },
  meta,
});

export const genericFetchInterventionsSuccess = <U = PaginatedIntervention>(
  payload: PageContent<U>,
  meta: Record<string, unknown> = {},
): types.FetchInterventionsSuccessAction<U> => ({
    type: actionTypes.INTERVENTIONS_FETCH_SUCCESS,
    payload,
    meta,
  });

export const genericFetchInterventionsFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.FetchInterventionsFailureAction => ({
  type: actionTypes.INTERVENTIONS_FETCH_FAILURE,
  payload: error,
  error: true,
  meta,
});

// CRUD OPERATIONS
export const createInterventionRequest = (
  data: InterventionsCreateRequest & { mode: ModeEnum },
  meta: Record<string, unknown> = {},
): types.CreateInterventionRequestAction => ({
  type: actionTypes.CREATE_INTERVENTION_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: INTERVENTIONS_API_URL[data.mode],
      data,
    }
  },
  meta,
});

export const createInterventionSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.CreateInterventionSuccessAction => ({
  type: actionTypes.CREATE_INTERVENTION_SUCCESS,
  payload: intervention,
  meta
});

export const createInterventionFailure = (error: string, meta: Record<string, unknown> = {}): types.CreateInterventionFailureAction => ({
  type: actionTypes.CREATE_INTERVENTION_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const updateInterventionRequest = (
  { depositPercentage, depositAmount, ...intervention }: Omit<DetailedIntervention, 'mode'> & { mode: InterventionApiMode },
  params?: types.UpdateInterventionRequestActionPayload,
  meta: Record<string, unknown> = {},
): types.UpdateInterventionRequestAction => ({
  type: actionTypes.UPDATE_INTERVENTION_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${INTERVENTIONS_API_URL[intervention.mode]}/${intervention.id}`,
      data: {
        ...intervention,
        depositPercentage: depositPercentage || undefined,
        depositAmount: depositAmount || undefined,
      },
      params,
    },
  },
  meta,
});

export const updateInterventionSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.UpdateInterventionSuccessAction => ({
  type: actionTypes.UPDATE_INTERVENTION_SUCCESS,
  payload: intervention,
  meta,
});

export const updateInterventionFailure = (
  message: string,
  meta: Record<string, unknown> = {},
): types.UpdateInterventionFailureAction => ({
  type: actionTypes.UPDATE_INTERVENTION_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const deleteIntervention = (
  interventionId: string,
  mode: InterventionApiMode,
  meta: Record<string, unknown> = {},
): types.DeleteInterventionRequestAction => ({
  type: actionTypes.DELETE_INTERVENTION_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}`,
    },
    interventionId,
  },
  meta,
});

export const deleteInterventionSuccess = (
  interventionId: string,
  meta?: Record<string, unknown>,
): types.DeleteInterventionSuccessAction => ({
  type: actionTypes.DELETE_INTERVENTION_SUCCESS,
  payload: { interventionId },
  meta,
});

export const deleteInterventionFailure = (error: string, meta: Record<string, unknown> = {}): ErrorAction => ({
  type: actionTypes.DELETE_INTERVENTION_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const patchStatus = (
  interventionId: string,
  mode: InterventionApiMode,
  status: InterventionStatus,
  meta: Record<string, unknown> = {},
): types.PatchInterventionStatusAction => ({
  type: actionTypes.UPDATE_INTERVENTION_STATUS_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}/status`,
      data: { status },
    },
  },
  meta,
});

export const updateInterventionStatusFailure = (
  message: string,
  meta: Record<string, unknown> = {},
): types.UpdateInterventionStatusFailureAction => ({
  type: actionTypes.UPDATE_INTERVENTION_STATUS_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const updateInterventionStatusSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.UpdateInterventionStatusSuccessAction => ({
  type: actionTypes.UPDATE_INTERVENTION_STATUS_SUCCESS,
  payload: intervention,
  meta
});

export const postDuplicateIntervention = (
  interventionId: string,
  mode: InterventionApiMode,
  data: InterventionCloneOptions,
): types.DuplicateInterventionAction => ({
  type: actionTypes.POST_DUPLICATE_INTERVENTION,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}/clone`,
      data,
    },
  },
});

export const postDuplicateInterventionSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.DuplicateInterventionSuccessAction => ({
  type: actionTypes.POST_DUPLICATE_INTERVENTION_SUCCESS,
  payload: intervention,
  meta
});

export const postDuplicateInterventionError = (message: string): types.DuplicateInterventionFailureAction => ({
  type: actionTypes.POST_DUPLICATE_INTERVENTION_FAILURE,
  payload: {
    message,
  },
  error: true,
});

// WORK ORDER

export const getWorkByWorkOrder = (
  interventionId: string,
  mode: InterventionApiMode,
  orderNumber: string,
  reference: string,
  dealerId: string,
  meta: Record<string, unknown> = {},
): types.GetWorkByWorkOrder => ({
  type: actionTypes.GET_WORKS_ORDER_BY_REPARATION_ORDER,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}/workOrder/search`,
      params: { dealerId, reference, orderNumber },
    },
    interventionId,
  },
  meta,
});

export const fetchWorkByWorkOrderSuccess = (
  interventionId: string,
  workOrder: string,
  checklists: Array<DetailedChecklist>,
  meta: Record<string, unknown> = {},
): types.GetWorksOrderByORSuccess => ({
  type: actionTypes.FETCH_WORKS_ORDER_SUCCESS,
  payload: { interventionId, workOrder, checklists },
  meta,
});

export const fetchWorkByWorkOrderFailure = (error: string): types.GetWorksOrderByORFailure => ({
  type: actionTypes.FETCH_WORKS_ORDER_FAILURE,
  payload: error,
  error: true,
});

export const patchInterventionTags = (
  { interventionId, mode, tagIds }: { interventionId: string; mode: InterventionApiMode; tagIds: string[] },
  meta?: Record<string, unknown>,
): types.PatchInterventionTagsAction => ({
  type: actionTypes.UPDATE_INTERVENTION_TAGS,
  payload: {
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}/tags`,
      data: {
        tagIds,
      },
    },
  },
  meta,
});

/**
 * Evaluate intervention tags based on configured rules
 */
export const evaluateInterventionTags = (
  { interventionId, mode }: { interventionId: string; mode: InterventionApiMode },
  meta: Record<string, unknown> = {},
): types.EvaluateInterventionTagsAction => ({
  type: actionTypes.EVALUATE_INTERVENTION_TAGS,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}/tags/evaluate`,
    },
    interventionId,
    mode,
  },
  meta,
});
